/**
 * Projects the lifecycle machine's phase into the editor's lifecycle vocabulary.
 *
 * Nothing here reads or mutates runtime state, and nothing here is authoritative: callers pass the
 * machine phase they already read and get back the editor phase it describes.
 */
import type { RemixPhase } from '../../state/types';
import type { EditorRemixLifecyclePhase } from './types';

/**
 * The machine's phases map one-to-one onto the editor's vocabulary. Unlike the renderer, the editor
 * has a real `inserting` phase of its own, so nothing is relabeled here.
 */
const MACHINE_TO_EDITOR_PHASE = {
	thinking: 'thinking',
	ready: 'ready',
	previewing: 'previewing',
	inserting: 'inserting',
	error: 'error',
	aborted: 'aborted',
} as const satisfies Record<RemixPhase, EditorRemixLifecyclePhase>;

/**
 * Projects a machine phase into the editor's vocabulary.
 *
 * An absent phase is `inactive`, which covers both "no flow has been admitted yet" and "the flow's
 * machine has already been destroyed". Callers that need to survive that teardown must capture the
 * phase before releasing machine ownership rather than re-reading it afterwards.
 *
 * An unmapped phase is also `inactive`. The machine's `state()` is typed `RemixPhase`, but its
 * actual state set is wider — `done` is a real state that `RemixPhase` omits — so a phase outside
 * the map is reachable at runtime even though the type says it is not. No caller can observe `done`
 * today, because the flow controller destroys the machine on reaching it, but returning `undefined`
 * through a signature that forbids it would be a silent lie if that ever changed.
 */
export const deriveMachineEditorRemixPhase = (
	phase: RemixPhase | undefined,
): EditorRemixLifecyclePhase => {
	if (phase === undefined) {
		return 'inactive';
	}

	// Cast widens the key type only; `satisfies` above still requires every `RemixPhase` to be
	// mapped, so this cannot mask a genuinely missing entry for a declared phase.
	const mapped = (MACHINE_TO_EDITOR_PHASE as Record<string, EditorRemixLifecyclePhase | undefined>)[
		phase
	];
	return mapped ?? 'inactive';
};

/** A phase from which no further lifecycle transition is expected. */
export type EditorRemixTerminalLifecyclePhase = Extract<
	EditorRemixLifecyclePhase,
	'error' | 'aborted'
>;

export const isTerminalEditorRemixPhase = (
	phase: EditorRemixLifecyclePhase,
): phase is EditorRemixTerminalLifecyclePhase => phase === 'error' || phase === 'aborted';
